import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { Package, AlertTriangle, CheckCircle2, Zap, Activity, Wrench, ChevronRight, ChevronLeft, LayoutDashboard } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import dayjs from 'dayjs';
import LottieAnim from '../components/LottieAnim';

const STATUS_LABEL = {
  pending: '대기',
  stl_ready: 'STL 확인',
  processing: '스케일링',
  queued: '큐 대기',
  printing: '출력 중',
  completed: '완료',
  failed: '실패',
  cancelled: '취소',
};

const STATUS_COLOR = {
  idle: 'bg-green-500',
  printing: 'bg-brand',
  paused: 'bg-yellow-500',
  error: 'bg-red-500',
  offline: 'bg-gray-600',
};

function StatCard({ icon: Icon, label, value, color = 'text-white' }) {
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
      <div className="flex items-center gap-3 mb-3">
        <Icon size={18} className="text-gray-500" />
        <span className="text-gray-400 text-sm">{label}</span>
      </div>
      <p className={`text-3xl font-bold ${color}`}>{value}</p>
    </div>
  );
}

export default function DashboardPage() {
  const [summary, setSummary] = useState(null);
  const [printers, setPrinters] = useState([]);
  const [mqttMap, setMqttMap] = useState({});
  const [recentJobs, setRecentJobs] = useState([]);
  const [jobPage, setJobPage] = useState(1);
  const [jobTotal, setJobTotal] = useState(0);
  const JOB_PAGE_SIZE = 10;
  const [kpi, setKpi] = useState(null);
  const [failureCount, setFailureCount] = useState(0);
  const [maintenanceAlerts, setMaintenanceAlerts] = useState([]);
  const today = dayjs().format('YYYY-MM-DD');

  useEffect(() => {
    api.get(`/jobs/summary/daily?date=${today}`).then(r => setSummary(r.data));
    api.get('/printers').then(r => setPrinters(r.data));
    api.get('/printers/monitor/status').then(r => {
      const m = {};
      r.data.forEach(s => { m[s.printerId] = s; });
      setMqttMap(m);
    }).catch(() => {});
    api.get(`/jobs?limit=${JOB_PAGE_SIZE}&offset=0`).then(r => { setRecentJobs(r.data.jobs); setJobTotal(r.data.total || 0); });
    api.get('/analytics/kpi?period=day').then(r => setKpi(r.data)).catch(() => {});
    api.get('/failures?unresolvedOnly=true&limit=1').then(r => {
      if (Array.isArray(r.data)) setFailureCount(r.data.length);
      else setFailureCount(r.data?.total || 0);
    }).catch(() => {});
    api.get('/maintenance/alerts').then(r => setMaintenanceAlerts(Array.isArray(r.data) ? r.data : [])).catch(() => {});

    const t = setInterval(() => {
      api.get('/printers/monitor/status').then(r => {
        const m = {};
        r.data.forEach(s => { m[s.printerId] = s; });
        setMqttMap(m);
      }).catch(() => {});
    }, 15000);
    return () => clearInterval(t);
  }, []);

  const loadJobPage = (page) => {
    const offset = (page - 1) * JOB_PAGE_SIZE;
    api.get(`/jobs?limit=${JOB_PAGE_SIZE}&offset=${offset}`).then(r => {
      setRecentJobs(r.data.jobs);
      setJobTotal(r.data.total || 0);
      setJobPage(page);
    });
  };

  const activePrinters = printers.filter(p => mqttMap[p.id]?.gcodeState === 'RUNNING').length;
  const idlePrinters = printers.filter(p => !mqttMap[p.id] || mqttMap[p.id]?.gcodeState !== 'RUNNING').length;

  return (
    <div>
      <PageHeader icon={LayoutDashboard} title="대시보드" />

      {/* 경고 배너 — 실패/PM 이슈 */}
      {(failureCount > 0 || maintenanceAlerts.length > 0) && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl px-4 py-3 mb-4 flex flex-wrap items-center gap-2">
          <AlertTriangle size={16} className="text-yellow-400 flex-shrink-0" />
          <span className="text-gray-400 text-xs mr-2">주의 필요</span>
          {failureCount > 0 && (
            <Link to="/failures" className="flex items-center gap-1.5 bg-red-500/10 border border-red-500/30 hover:bg-red-500/20 rounded-lg px-3 py-1.5 transition-colors">
              <span className="text-red-400 text-xs">실패 감지</span>
              <span className="text-white text-xs font-semibold">{failureCount}건</span>
              <ChevronRight size={12} className="text-red-400/60" />
            </Link>
          )}
          {maintenanceAlerts.length > 0 && (
            <Link to="/maintenance" className="flex items-center gap-1.5 bg-orange-500/10 border border-orange-500/30 hover:bg-orange-500/20 rounded-lg px-3 py-1.5 transition-colors">
              <Wrench size={12} className="text-orange-400" />
              <span className="text-orange-400 text-xs">정비 필요</span>
              <span className="text-white text-xs font-semibold">{maintenanceAlerts.length}건</span>
              <ChevronRight size={12} className="text-orange-400/60" />
            </Link>
          )}
        </div>
      )}

      {/* 통계 카드 */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <StatCard icon={Package} label="오늘 주문" value={summary?.total ?? '-'} />
        <StatCard icon={Zap} label="출력 중" value={activePrinters} color="text-brand" />
        <StatCard icon={CheckCircle2} label="완료" value={summary?.completed ?? '-'} color="text-green-400" />
        <StatCard icon={AlertTriangle} label="실패" value={summary?.failed ?? '-'} color="text-red-400" />
        <Link to="/analytics" className="block">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 hover:border-brand/50 transition-colors h-full">
            <div className="flex items-center gap-3 mb-3">
              <Activity size={18} className="text-gray-500" />
              <span className="text-gray-400 text-sm">OEE (오늘)</span>
            </div>
            <p className="text-3xl font-bold text-brand">
              {kpi?.oee?.overall != null ? `${kpi.oee.overall}%` : '-'}
            </p>
          </div>
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {/* 프린터 현황 */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-white font-semibold text-sm">프린터 현황</h2>
            <div className="flex gap-3 text-xs text-gray-500">
              <span className="text-green-400">● 출력 중 {activePrinters}</span>
              <span>대기 {idlePrinters}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {[...printers].sort((a, b) => {
              const aRun = mqttMap[a.id]?.gcodeState === 'RUNNING' ? 0 : 1;
              const bRun = mqttMap[b.id]?.gcodeState === 'RUNNING' ? 0 : 1;
              if (aRun !== bRun) return aRun - bRun;
              return (mqttMap[a.id]?.mcRemainingTime ?? Infinity) - (mqttMap[b.id]?.mcRemainingTime ?? Infinity);
            }).slice(0, 20).map(p => {
              const mqtt = mqttMap[p.id];
              const isPrinting = mqtt?.gcodeState === 'RUNNING';
              const isFinished = mqtt?.gcodeState === 'FINISH';
              const realStatus = isPrinting ? 'printing' : p.status;
              return (
                <div key={p.id} className="bg-gray-800 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`w-2 h-2 rounded-full ${STATUS_COLOR[realStatus] || 'bg-gray-600'}`} />
                    <span className="text-white text-xs font-medium truncate">{p.name}</span>
                    {p.type === 'klipper' && (
                      <span className="text-[9px] px-1 py-px rounded bg-purple-500/20 text-purple-400 flex-shrink-0">K</span>
                    )}
                  </div>
                  {isPrinting && mqtt.mcPercent != null && (
                    <div className="mb-1">
                      <div className="flex items-center justify-between text-xs mb-0.5">
                        <span className="text-brand font-bold">{mqtt.mcPercent}%</span>
                        {mqtt.mcRemainingTime > 0 && (
                          <span className="text-gray-500">{Math.floor(mqtt.mcRemainingTime / 60)}h {mqtt.mcRemainingTime % 60}m</span>
                        )}
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-1">
                        <div className="h-1 rounded-full bg-brand" style={{ width: `${mqtt.mcPercent}%` }} />
                      </div>
                    </div>
                  )}
                  {isFinished && (
                    <div className="flex items-center gap-1 mb-1">
                      <LottieAnim type="success" size={20} />
                      <p className="text-emerald-400 text-xs">출력 완료</p>
                    </div>
                  )}
                  {mqtt && (
                    <p className="text-gray-500 text-xs">
                      노즐 {mqtt.nozzleTemper ?? '--'}° · 베드 {mqtt.bedTemper ?? '--'}°
                      {mqtt.nozzle2Temper != null && ` · 노즐2 ${mqtt.nozzle2Temper}°`}
                    </p>
                  )}
                  {!mqtt && (
                    <p className="text-gray-500 text-xs">
                      {p.filamentSlots?.[0]?.colorName || '슬롯 미설정'}
                    </p>
                  )}
                </div>
              );
            })}
            {printers.length === 0 && (
              <div className="col-span-3 flex flex-col items-center py-6">
                <LottieAnim type="empty" size={80} />
                <p className="text-gray-600 text-sm mt-2">등록된 프린터가 없습니다.</p>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* 최근 잡 */}
      <div className="mt-4 bg-gray-900 border border-gray-800 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white font-semibold text-sm">최근 생산 잡</h2>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span>총 {jobTotal}건</span>
            <div className="flex items-center gap-1">
              <button onClick={() => loadJobPage(jobPage - 1)} disabled={jobPage <= 1}
                className="p-1 rounded hover:bg-gray-800 disabled:opacity-30">
                <ChevronLeft size={14} />
              </button>
              <span className="text-gray-400">{jobPage} / {Math.max(1, Math.ceil(jobTotal / JOB_PAGE_SIZE))}</span>
              <button onClick={() => loadJobPage(jobPage + 1)} disabled={jobPage >= Math.ceil(jobTotal / JOB_PAGE_SIZE)}
                className="p-1 rounded hover:bg-gray-800 disabled:opacity-30">
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-500 text-xs border-b border-gray-800 whitespace-nowrap">
                <th className="text-left pb-2 pr-3">잡 ID</th>
                <th className="text-left pb-2 pr-3">주문번호</th>
                <th className="text-left pb-2 pr-3">수령인</th>
                <th className="text-left pb-2 pr-3">상품</th>
                <th className="text-left pb-2 pr-3">사이즈</th>
                <th className="text-left pb-2 pr-3">프린터</th>
                <th className="text-left pb-2 pr-3">상태</th>
                <th className="text-left pb-2">접수</th>
              </tr>
            </thead>
            <tbody>
              {recentJobs.map(j => (
                <tr key={j.id} className="border-b border-gray-800/50 hover:bg-gray-800/50 whitespace-nowrap">
                  <td className="py-2 pr-3 text-gray-400">#{j.id}</td>
                  <td className="py-2 pr-3 text-gray-300">{j.xinvanaOrderNumber || '-'}</td>
                  <td className="py-2 pr-3 text-gray-300">{j.recipientName || '-'}</td>
                  <td className="py-2 pr-3 text-gray-300 max-w-[120px] truncate">{j.productName || `상품 #${j.productId}`}</td>
                  <td className="py-2 pr-3 text-gray-400 font-mono text-xs">L:{j.leftSizeCode} / R:{j.rightSizeCode}</td>
                  <td className="py-2 pr-3 text-gray-400">{j.printer?.name || '-'}</td>
                  <td className="py-2 pr-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      j.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                      j.status === 'printing' ? 'bg-brand/20 text-brand' :
                      j.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                      'bg-gray-700 text-gray-400'
                    }`}>
                      {STATUS_LABEL[j.status]}
                    </span>
                  </td>
                  <td className="py-2 text-gray-500 text-xs">{dayjs(j.createdAt).format('HH:mm')}</td>
                </tr>
              ))}
              {recentJobs.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-6 text-center">
                    <div className="flex flex-col items-center">
                      <LottieAnim type="empty" size={72} />
                      <span className="text-gray-600 text-sm mt-1">잡 없음</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
